import { useQueryClient, useMutation } from "@tanstack/react-query";

import i18n from "@/lib/i18n";
import {
  CreateDashboardBody,
  UpdateDashboardBody,
  UpdateTimeZoneSettingsUpdateBody,
  UpdateTimeRangeSelectorSettingsUpdateBody,
  api,
  UpdateExtranetBody,
} from "@/lib/api";
import { removePublishedDashboardQueries } from "@/features/dashboard/features/dashboard";
import { fetchPublishedNavigationElements } from "@/features/dashboard/published-navigation";
import { publishedDashboardsQuery, queryKeys } from "@/features/shared/hooks";
import { showToast } from "@/features/shared/components";

export const useCreateDashboardMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ extranetId, body }: { extranetId: string; body: CreateDashboardBody }) =>
      api.dashboard.createDashboard(extranetId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.dashboards(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.publishedDashboards(),
      });
    },
  });
};

export const useUpdateDashboardMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateDashboardBody }) =>
      api.dashboard.updateDashboard(id, body),
    onSuccess: ({ data }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.dashboards(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.dashboardDetail(data.id),
      });
    },
  });
};

export const useDeleteDashboardMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dashboardId: string) => api.dashboard.deleteDashboard(dashboardId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.dashboards(),
      });
    },
  });
};

export const usePublishDashboardMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dashboardId: string) => api.dashboard.publishDashboard(dashboardId),
    onSuccess: (_, dashboardId) => {
      // Publishing can change anything about a dashboard, so reset the whole
      // ["dashboard", id] subtree (detail, locations, elements, warnings, ...).
      queryClient.invalidateQueries({
        queryKey: queryKeys.dashboard(dashboardId),
      });
      removePublishedDashboardQueries(queryClient, dashboardId);

      // Warm what the dashboard app's page loader needs, so following the
      // header link straight after publishing usually joins a request that is
      // already in flight instead of waiting for (or racing) a cold one.
      // Fire-and-forget: failures are the loader's problem, not the dialog's.
      const ignore = () => {};
      queryClient.fetchQuery({ ...publishedDashboardsQuery(), staleTime: 0 }).catch(ignore);
      fetchPublishedNavigationElements(queryClient, dashboardId).catch(ignore);
    },
  });
};

export const useTimeRangeSelectorMutation = (dashboardId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: UpdateTimeRangeSelectorSettingsUpdateBody) =>
      api.timeRangeSelector.updateTimeRangeSelectorSettings(dashboardId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.timerange(dashboardId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.dashboardDetail(dashboardId),
      });
      showToast(i18n.t("admin.dashboard_settings.time_settings.success"));
    },
    onError: () => {
      showToast(i18n.t("admin.dashboard_settings.time_settings.error"));
    },
  });
};

export const useTimezoneMutation = (dashboardId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: UpdateTimeZoneSettingsUpdateBody) =>
      api.timezones.updateTimeZoneSettings(dashboardId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.timezone(dashboardId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.dashboardDetail(dashboardId),
      });
      showToast(i18n.t("admin.dashboard_settings.time_settings.success"));
    },
    onError: () => {
      showToast(i18n.t("admin.dashboard_settings.time_settings.error"));
    },
  });
};

export const useExtranetMutation = (extranetId: string) => {
  return useMutation({
    mutationKey: ["extranet", extranetId],
    mutationFn: async (values: UpdateExtranetBody) => {
      return await api.extranets.updateExtranet(extranetId, values);
    },
    onSuccess: () => {
      showToast(i18n.t("admin.extranets.update.success"));
    },
    onError: () => {
      showToast(i18n.t("admin.extranets.update.error"));
    },
  });
};
